/**
 * OauthLoginController — OAuth 로그인 탭 전환 컨트롤러
 *
 * OAuth 인증 페이지에서 두 가지 로그인 방식을 탭으로 전환
 *
 * 탭 구조:
 *   탭 1 (Google): Google OAuth 로그인
 *   탭 2 (App Password): 이메일 + 앱 비밀번호 입력 폼
 *
 * 타겟:
 *   googleTab   — Google 탭 버튼
 *   appTab      — App Password 탭 버튼
 *   googlePanel — Google 로그인 패널 (컨텐츠 영역)
 *   appPanel    — App Password 로그인 패널 (컨텐츠 영역)
 *
 * 사이버펑크 컬러:
 *   활성 탭: #39FF14 (네온 그린) 하단 보더 + 밝은 텍스트
 *   비활성 탭: #555577 dim 텍스트
 *
 * 사용법:
 *   data-controller="oauth-login"
 *   data-action="click->oauth-login#switchToGoogle"
 *   data-oauth-login-target="googleTab"
 */
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  // 탭 버튼과 콘텐츠 패널 타겟 정의
  static targets = [
    "googleTab",        // Google 로그인 탭 버튼
    "appTab",           // App Password 탭 버튼
    "googlePanel",      // Google 로그인 콘텐츠 패널
    "appPanel",         // App Password 로그인 콘텐츠 패널
    "alternativePanel"  // 세션 감지 시 "다른 계정으로 로그인" 접기/펼치기 패널
  ]

  connect() {
    if (this.hasGoogleTabTarget) this.showGoogleTab()
  }

  toggleAlternative() {
    if (!this.hasAlternativePanelTarget) return
    const panel = this.alternativePanelTarget
    panel.style.display = panel.style.display === "none" ? "block" : "none"
  }

  switchToGoogle() {
    this.showGoogleTab()
  }

  // ── App Password 탭으로 전환 ────────────────────────────
  // 클릭 이벤트: data-action="click->oauth-login#switchToApp"
  switchToApp() {
    this.showAppTab()
  }

  // ── 내부: Google 탭 표시 ────────────────────────────────
  showGoogleTab() {
    // Google 패널 표시, App 패널 숨김
    this.googlePanelTarget.style.display = "block"
    this.appPanelTarget.style.display    = "none"

    // Google 탭 활성 스타일 (네온 그린 하단 보더)
    this.setActiveTab(this.googleTabTarget)
    this.setInactiveTab(this.appTabTarget)
  }

  // ── 내부: App Password 탭 표시 ──────────────────────────
  showAppTab() {
    // App 패널 표시, Google 패널 숨김
    this.googlePanelTarget.style.display = "none"
    this.appPanelTarget.style.display    = "block"

    // App 탭 활성 스타일 (네온 그린 하단 보더)
    this.setActiveTab(this.appTabTarget)
    this.setInactiveTab(this.googleTabTarget)
  }

  // ── 내부: 탭 활성 스타일 적용 ────────────────────────────
  // 사이버펑크 네온 그린 (#39FF14) 하단 보더 + 밝은 텍스트
  setActiveTab(tab) {
    tab.style.borderBottom = "2px solid #39FF14"
    tab.style.color        = "#E0E0E0"
    tab.style.opacity      = "1"
    tab.setAttribute("aria-selected", "true")
  }

  // ── 내부: 탭 비활성 스타일 적용 ─────────────────────────
  // dim 처리 (#555577 색상, 하단 보더 제거)
  setInactiveTab(tab) {
    tab.style.borderBottom = "2px solid transparent"
    tab.style.color        = "#555577"
    tab.style.opacity      = "0.6"
    tab.setAttribute("aria-selected", "false")
  }
}
